/**
 * Shared anxiety rating scale configuration.
 *
 * The 0-8 scale is used in three places:
 *   - Free self-assessment (after each video)
 *   - Paid programme (after each daily viewing)
 *   - Onboarding (mobile severity slider, will be removed in Sprint C)
 *
 * Single source of truth for labels, band ranges, and colors so all
 * platform-specific components present the user with the same scale.
 */

export const RATING_MIN = 0;
export const RATING_MAX = 8;
export type Rating = number; // integer 0..8

/**
 * Per-number labels (legacy fine-grained). Used in the "selected" display
 * underneath the rating buttons.
 */
export const RATING_LABELS: Record<number, string> = {
  0: 'No anxiety',
  1: 'Minimal',
  2: 'Mild',
  3: 'Low',
  4: 'Moderate',
  5: 'Medium',
  6: 'Elevated',
  7: 'High',
  8: 'Severe',
};

/**
 * Three coarser bands shown alongside the scale (per Sprint A brief).
 *   - Low Anxiety: 0–3
 *   - Medium Anxiety: 4–5
 *   - High Anxiety: 6–8
 *
 * These band ranges are also the source of truth used by the upcoming
 * Sprint B logic rewrite (medium-to-high = Suitable; consistently low = Not Suitable).
 */
export type AnxietyBand = 'low' | 'medium' | 'high';

export interface AnxietyBandConfig {
  id: AnxietyBand;
  label: string;
  rangeLabel: string; // e.g. "0–3"
  min: number;
  max: number;
  color: string; // hex; works in both web (Tailwind arbitrary value) and RN
}

const LOW_BAND: AnxietyBandConfig = {
  id: 'low',
  label: 'Low Anxiety',
  rangeLabel: '0–3',
  min: 0,
  max: 3,
  color: '#6B9D7A',
};
const MEDIUM_BAND: AnxietyBandConfig = {
  id: 'medium',
  label: 'Medium Anxiety',
  rangeLabel: '4–5',
  min: 4,
  max: 5,
  color: '#D0A05B',
};
const HIGH_BAND: AnxietyBandConfig = {
  id: 'high',
  label: 'High Anxiety',
  rangeLabel: '6–8',
  min: 6,
  max: 8,
  color: '#C7796B',
};

export const ANXIETY_BANDS: readonly AnxietyBandConfig[] = [LOW_BAND, MEDIUM_BAND, HIGH_BAND];

export function getBandForRating(rating: number): AnxietyBandConfig {
  return ANXIETY_BANDS.find((b) => rating >= b.min && rating <= b.max) ?? LOW_BAND;
}

const LOW_COLOR = LOW_BAND.color;
const MEDIUM_COLOR = MEDIUM_BAND.color;
const HIGH_COLOR = HIGH_BAND.color;

/**
 * Build the three bands dynamically from the admin-configured thresholds.
 *
 *   Low band:    0 .. lowThreshold
 *   Medium band: lowThreshold+1 .. mediumThreshold
 *   High band:   mediumThreshold+1 .. RATING_MAX
 *
 * The DB CHECK constraint guarantees `low_threshold < medium_threshold`, but
 * we defend against degenerate values (e.g. medium == RATING_MAX) by
 * dropping any band whose range is empty.
 */
export function buildBandsFromThresholds(
  lowThreshold: number,
  mediumThreshold: number,
): readonly AnxietyBandConfig[] {
  const lo = clamp(lowThreshold, RATING_MIN, RATING_MAX);
  const mid = clamp(mediumThreshold, lo, RATING_MAX);

  const formatRange = (min: number, max: number): string =>
    min === max ? `${min}` : `${min}–${max}`;

  const bands: AnxietyBandConfig[] = [];
  // Low: 0..lo
  bands.push({
    id: 'low',
    label: 'Low Anxiety',
    rangeLabel: formatRange(RATING_MIN, lo),
    min: RATING_MIN,
    max: lo,
    color: LOW_COLOR,
  });
  // Medium: lo+1..mid (only if non-empty)
  if (mid > lo) {
    bands.push({
      id: 'medium',
      label: 'Medium Anxiety',
      rangeLabel: formatRange(lo + 1, mid),
      min: lo + 1,
      max: mid,
      color: MEDIUM_COLOR,
    });
  }
  // High: mid+1..MAX (only if non-empty)
  if (mid < RATING_MAX) {
    bands.push({
      id: 'high',
      label: 'High Anxiety',
      rangeLabel: formatRange(mid + 1, RATING_MAX),
      min: mid + 1,
      max: RATING_MAX,
      color: HIGH_COLOR,
    });
  }
  return bands;
}

export function getBandForRatingDynamic(
  rating: number,
  bands: readonly AnxietyBandConfig[],
): AnxietyBandConfig {
  return bands.find((b) => rating >= b.min && rating <= b.max) ?? bands[0] ?? LOW_BAND;
}

export function getRatingButtonColorDynamic(
  rating: number,
  isSelected: boolean,
  bands: readonly AnxietyBandConfig[],
): string {
  if (!isSelected) return '#F6F3EC';
  return getBandForRatingDynamic(rating, bands).color;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Background hex for a rating button. Always returns a hex string usable
 * by Tailwind arbitrary value (`bg-[#...]`) and React Native (`backgroundColor: ...`).
 */
export function getRatingButtonColor(rating: number, isSelected: boolean): string {
  if (!isSelected) return '#F6F3EC';
  return getBandForRating(rating).color;
}
